/**
 * Project cards component for home page and projects listing page.
 *
 * @category components
 */

import { useEffect, useMemo, useState } from "react";
import { t } from "@lingui/core/macro";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { useUser } from "_/data/users";
import { groupById } from "_/utils";

import { Skeleton } from "_/components/skeleton";
import { UserAvatar } from "_/components/avatar";
import { RelativeDateTime } from "_/components/date-time";
import { Icon } from "_/components/icon";
import { Tooltip } from "_/components/tooltip";
import { H5 } from "_/components/typography";

import {
  Attempt,
  Project,
  sortAttempts,
  useAllAttempts,
  useProjects,
} from "_/data/projects";

import * as S from "./styled";
import { projectUrls } from "_/routes";
import archiveTooltip from "_/assets/img/archiveTooltip.png";
import { Uuid } from "_/types";

dayjs.extend(relativeTime);

// Placeholder image of a wireframe cube, used for project previews.
//
// If the `$pulse` prop is set to `true`, the cube will have a gentle pulse
// animation applied.
const ProjectPlaceholderImage = ({
  $visibility,
  $pulse,
}: {
  $visibility: "hidden" | "visible";
  $pulse: boolean;
}) => (
  <S.ProjectPlaceholderSVG
    $visibility={$visibility}
    $pulse={$pulse}
    width="960"
    height="960"
    viewBox="0 0 960 960"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    xmlns="http://www.w3.org/2000/svg"
  >
    <line x1="333" y1="37" x2="333" y2="628" />
    <line x1="43" y1="332" x2="43" y2="921" />
    <line x1="917" y1="628" x2="917" y2="37" />
    <line x1="627" y1="921" x2="627" y2="332" />
    <line x1="628" y1="331" x2="42" y2="331" />
    <line x1="918" y1="38" x2="332" y2="38" />
    <line x1="332" y1="627" x2="918" y2="627" />
    <line x1="42" y1="920" x2="628" y2="920" />
    <line x1="332.711" y1="37.7035" x2="42.7107" y2="330.703" />
    <line x1="333.711" y1="626.703" x2="43.7107" y2="919.703" />
    <line x1="916.479" y1="38.3338" x2="626.711" y2="331.703" />
    <line x1="917.313" y1="627.205" x2="627.252" y2="920.217" />
  </S.ProjectPlaceholderSVG>
);

/**
 * How long after project creation we assume a preview may still be generating.
 * This typically takes just a few seconds, but may be delayed during periods
 * of heavy worker load.
 */
const FRESH_WINDOW_MS = 30_000;
const POLL_INTERVAL_MS = 2_000;

// Project preview image.
//
// This will attempt to load the project preview, and if the image is not yet ready (e.g.
// immediately after project creation), a placeholder will be shown with a subtle pulsing
// animation to show that the image is being generated. If the image has not been generated
// after the `FRESH_WINDOW_MS` interval, it will become static and assumed that the preview
// generation has failed.
const ProjectPreview = ({ project }: { project: Project }) => {
  const [loaded, setLoaded] = useState(false);

  const thumbnailUrl = `/api/v0/projects/${project.id}/thumbnail?q=${project.updatedAt}`;

  const isFresh = dayjs().diff(dayjs(project.createdAt)) < FRESH_WINDOW_MS;
  const isGenerating = !loaded && isFresh;

  useEffect(() => {
    if (!isGenerating) {
      return undefined;
    }

    const id = setInterval(() => {
      if (!isFresh) {
        clearInterval(id);
        return;
      }

      const probe = new Image();
      probe.onload = () => setLoaded(true);
      probe.src = thumbnailUrl;
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(id);
    };
  }, [isGenerating, isFresh, thumbnailUrl]);

  return (
    <S.ProjectPreviewContainer>
      <ProjectPlaceholderImage
        $visibility={loaded ? "hidden" : "visible"}
        $pulse={isGenerating}
      />
      <S.ProjectPreviewImage
        key={loaded ? "loaded" : "pending"}
        src={thumbnailUrl}
        $visibility={loaded ? "visible" : "hidden"}
        onLoad={() => setLoaded(true)}
      />
    </S.ProjectPreviewContainer>
  );
};

const EmptyProjectsContainer = () => (
  <S.EmptyProjectsContainer>
    <Icon variant="Folder" />
    {t`common.no-projects`}
  </S.EmptyProjectsContainer>
);

const ProjectCard = ({
  project,
  attempts,
}: {
  project: Project;
  attempts: Attempt[];
}) => {
  const projectURI = projectUrls.index(project.id);
  const { data: user } = useUser({ id: project.ownerId });

  const lastAttempt = attempts.toSorted(sortAttempts)[0];

  return (
    <S.ProjectCard to={projectURI}>
      <S.FirstRowContainer>
        <S.ProjectName>{project.name}</S.ProjectName>
        {user ? (
          <div title={user?.name}>
            <UserAvatar resource={user} size="small" />
          </div>
        ) : null}
      </S.FirstRowContainer>
      <S.SecondRowContainer>
        <ProjectPreview project={project} />
        <S.CardMetaContainer>
          <S.CardMeta>
            <label>{t`common.modified-label-only`}</label>
            <div>
              <RelativeDateTime value={project.updatedAt} />
            </div>
          </S.CardMeta>
          <S.CardMeta>
            <label>{t`common.printed`}</label>
            <div>
              <RelativeDateTime
                value={lastAttempt?.startedAt}
                defaultValue={t`common.not-printed`}
              />
            </div>
          </S.CardMeta>
        </S.CardMetaContainer>
      </S.SecondRowContainer>
    </S.ProjectCard>
  );
};

export const ProjectCards = ({
  limit = 200,
  showArchived = false,
  layout = "plain",
}: {
  limit?: number;
  showArchived?: boolean;
  layout?: "grid" | "plain";
}) => {
  const { data: allAttempts = [], isLoading: attemptsLoading } =
    useAllAttempts();
  const { data: allProjects = [], isLoading: projectsLoading } = useProjects();

  const attemptsByRevision = useMemo(
    () => groupById(allAttempts, "revisionId"),
    [allAttempts]
  );

  const attemptsByProject = useMemo(() => {
    const m = new Map<Uuid, Attempt[]>();

    for (const p of allProjects) {
      const revisionIds = p.revisions.map((r) => r.id);
      const attempts = revisionIds.flatMap(
        (rid: Uuid) => attemptsByRevision[rid] ?? []
      );
      m.set(p.id, attempts);
    }
    return m;
  }, [allProjects, attemptsByRevision]);

  // Weight limit: 2/3 for active, 1/3 for archived (rounded up for active)
  // Temporary until we add pagination
  const splitLimit = (limitInner: number) => {
    const activeQuota = Math.ceil((limitInner * 2) / 3);
    const archivedQuota = Math.max(0, limitInner - activeQuota);
    return { activeQuota, archivedQuota };
  };
  const { active, archived } = useMemo(() => {
    const projects = [...allProjects].slice(0, limit);
    const activeAll: Project[] = [];
    const archivedAll: Project[] = [];

    for (const p of projects) {
      (p.status === "archived" ? archivedAll : activeAll).push(p);
    }

    // Apply weighted limits with rebalancing
    const { activeQuota, archivedQuota } = splitLimit(limit);

    const activeTake = Math.min(activeAll.length, activeQuota);
    const archivedTake = Math.min(archivedAll.length, archivedQuota);

    // Reallocate any unused quota
    const unusedActive = activeQuota - activeTake;
    const unusedArchived = archivedQuota - archivedTake;

    const finalActive = activeAll.slice(
      0,
      activeTake + Math.max(0, unusedArchived)
    );
    const finalArchived = archivedAll.slice(
      0,
      archivedTake + Math.max(0, unusedActive)
    );

    return { active: finalActive, archived: finalArchived };
  }, [allProjects, limit]);

  const isLoading = projectsLoading || attemptsLoading;

  const renderCards = (list: Project[]) =>
    list.map((p) => (
      <ProjectCard
        key={p.id}
        project={p}
        attempts={attemptsByProject.get(p.id) ?? []}
      />
    ));

  const renderSkeletons = () =>
    Array.from({ length: 5 }, (_, i) => (
      <Skeleton key={i} variant="block" style={{ height: "188px" }} />
    ));

  const renderList = (list: Project[]) => {
    const content = (() => {
      if (isLoading) return renderSkeletons();
      if (list.length > 0) return renderCards(list);
      return <EmptyProjectsContainer />;
    })();

    // Use plain layout when the parent already controls sizing/grid (e.g., home dashboard carousel)
    // to avoid nesting grids and clipping cards to a single column.
    return layout === "grid" ? (
      <S.ProjectCards>{content}</S.ProjectCards>
    ) : (
      <>{content}</>
    );
  };

  return (
    <>
      {renderList(active)}

      {showArchived && allProjects.length > 0 && (
        <>
          <S.ArchivedHeader>
            <H5>{t`common.archived`}</H5>
            <Tooltip
              placement="rightStart"
              delay={100}
              target={<Icon variant="Info" />}
              content={
                <div>
                  {t`components.project-cards.archive-tooltip`}
                  <img src={archiveTooltip} />
                </div>
              }
            />
          </S.ArchivedHeader>

          {renderList(archived)}
        </>
      )}
    </>
  );
};
